
import React from 'react';
import { ButtonCta } from '@/components/ui/ButtonCta';
import { ArrowRight, ChevronRight, ExternalLink } from '@/lib/icons';

interface CtaBlockProps {
  headline: string;
  subheadline?: string;
  buttonText: string;
  buttonHref: string;
  buttonSubtext?: string;
  background?: 'white' | 'light' | 'lightblue' | 'lightpurple' | 'gradient' | 'dark' | 'elevated' | 'blue';
  align?: 'left' | 'center' | 'right';
  size?: 'compact' | 'default' | 'large';
  icon?: 'arrow' | 'chevron' | 'external' | 'none';
  className?: string;
}

export const CtaBlock: React.FC<CtaBlockProps> = ({
  headline,
  subheadline,
  buttonText,
  buttonHref,
  buttonSubtext,
  background = 'light',
  align = 'center',
  size = 'default',
  icon = 'none',
  className = ''
}) => {
  // Background color mapping
  const backgroundMap: Record<string, string> = {
    white: 'bg-white',
    light: 'bg-neutral-50',
    lightblue: 'bg-blue-50',
    lightpurple: 'bg-purple-50',
    gradient: 'bg-gradient-to-br from-purple-50 via-white to-blue-50',
    dark: 'bg-neutral-900',
    elevated: 'bg-white shadow-2xl border-t-4 border-primary-500',
    blue: 'bg-[#294bfd]'
  };

  // Text alignment classes
  const alignMap: Record<string, string> = {
    left: 'text-left items-start',
    center: 'text-center items-center',
    right: 'text-right items-end'
  };

  // Size variants for padding and text
  const sizeMap = {
    compact: {
      container: 'py-8 md:py-12',
      headline: 'text-2xl md:text-3xl',
      subheadline: 'text-base',
      gap: 'space-y-4'
    },
    default: {
      container: 'py-12 md:py-16 lg:py-20',
      headline: 'text-3xl md:text-4xl',
      subheadline: 'text-lg',
      gap: 'space-y-6'
    },
    large: {
      container: 'py-16 md:py-24 lg:py-32',
      headline: 'text-4xl md:text-5xl',
      subheadline: 'text-xl',
      gap: 'space-y-8'
    }
  };

  // Icon component mapping
  const iconMap = {
    arrow: ArrowRight,
    chevron: ChevronRight,
    external: ExternalLink,
    none: null
  };

  const IconComponent = iconMap[icon];
  const bgClass = backgroundMap[background] || backgroundMap.light;
  const alignClass = alignMap[align] || alignMap.center;
  const sizeConfig = sizeMap[size] || sizeMap.default;
  const textColor = (background === 'dark' || background === 'blue') ? 'text-white' : 'text-gray-900';
  const subheadlineColor = (background === 'dark' || background === 'blue') ? 'text-gray-300' : 'text-gray-600';
  const subtextColor = (background === 'dark' || background === 'blue') ? 'text-white' : 'text-gray-500';

  return (
    <section className={`not-prose ${bgClass} rounded-lg ${sizeConfig.container} ${className}`}>
      <div className="container mx-auto px-4">
        <div className={`max-w-4xl mx-auto ${sizeConfig.gap} flex flex-col ${alignClass}`}>
          {/* Headline */}
          <h2 className={`${sizeConfig.headline} font-bold ${textColor} max-w-3xl`}>
            {headline}
          </h2>

          {/* Subheadline */}
          {subheadline && (
            <p className={`${sizeConfig.subheadline} ${subheadlineColor} max-w-2xl`}>
              {subheadline}
            </p>
          )}

          {/* CTA Button with optional icon */}
          <div className="flex flex-col items-center space-y-2">
            <ButtonCta 
              href={buttonHref}
              variant="primary"
              showArrow={true}
            >
              {buttonText}
            </ButtonCta>

            {/* Button subtext */}
            {buttonSubtext && (
              <p className={`text-sm ${subtextColor}`}>
                {buttonSubtext}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};